import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useAction } from 'convex/react'
import { useRef, useState } from 'react'

import { isTempId } from '@/store/offline.store'

export type ChatMessage = { role: 'user' | 'assistant'; content: string }

export function useTaskExplain() {
  const explainAction = useAction(api.voice.explainTask)
  const [isLoading, setIsLoading] = useState(false)
  const [streamedText, setStreamedText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<ChatMessage[]>([])
  // The question currently being answered, shown as a bubble until it lands in history.
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function clearStream() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // nodeId lets the server load the item's details (notes, schedule, number target,
  // today's log, streak) so the answer is about this item, not generic.
  async function ask(taskTitle: string, goalTitle: string, question: string, nodeId?: string) {
    clearStream()
    setIsLoading(true)
    setStreamedText('')
    setIsStreaming(false)
    setError(null)
    setPendingQuestion(question)
    try {
      const text = await explainAction({
        taskTitle,
        goalTitle,
        question,
        history,
        ...(nodeId && !isTempId(nodeId) && { nodeId: nodeId as Id<'nodes'> }),
      })
      setIsLoading(false)
      if (!text) return
      // Reveal ~250 chars/sec — matches real Claude/Grok streaming feel
      let i = 0
      setIsStreaming(true)
      intervalRef.current = setInterval(() => {
        i += 4
        if (i >= text.length) {
          setIsStreaming(false)
          clearStream()
          setHistory((prev) => [
            ...prev,
            { role: 'user', content: question },
            { role: 'assistant', content: text },
          ])
          setPendingQuestion(null)
          setStreamedText('')
        } else {
          setStreamedText(text.slice(0, i))
        }
      }, 16)
    } catch {
      setIsLoading(false)
      setError('Could not get a response. Please try again.')
    }
  }

  function reset() {
    clearStream()
    setStreamedText('')
    setIsStreaming(false)
    setError(null)
    setHistory([])
    setPendingQuestion(null)
  }

  return { ask, isLoading, streamedText, isStreaming, error, history, pendingQuestion, reset }
}
